import {
  ACCEPTED_PROOF_MIME_TYPES,
  MAX_PROOF_BYTES,
} from '@/constants/limits';

export const PROOF_REJECTION_REASONS = ['tooLarge', 'wrongType', 'empty'] as const;

export type ProofRejectionReason = (typeof PROOF_REJECTION_REASONS)[number];

const ACCEPTED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);

function extensionOf(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return null;
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Local mirror of the server's proof constraints. Exists to make a 400 rare, not to replace
 * server validation — a file that passes here can still be rejected, and the UI still handles that.
 *
 * MIME and extension are both required. iOS camera capture often reports `image/jpeg` with a `.JPG`
 * name; a MIME-only check is the right check. An extension-only check would accept a renamed PDF.
 * Requiring both is the cheapest way to make those two failure modes rare.
 */
export function validateProofFile(file: File): ProofRejectionReason | null {
  if (file.size === 0) return 'empty';
  if (file.size > MAX_PROOF_BYTES) return 'tooLarge';

  const mimeOk = (ACCEPTED_PROOF_MIME_TYPES as readonly string[]).includes(file.type);
  const ext = extensionOf(file.name);
  const extOk = ext !== null && ACCEPTED_EXTENSIONS.has(ext);

  if (!mimeOk || !extOk) return 'wrongType';
  return null;
}

/**
 * File-size display, not money. Uses 1024-based units because the server limit is 2 MiB, and
 * showing "2.1 MB" for a file that the server would reject as over 2 MiB would be a lie.
 *
 * Unit labels are the SI abbreviations (`KB`, `MB`), which are not translated — they are the same
 * in `en`/`es`/`pt` and the player already sees them in every file picker. The number is formatted
 * with the active locale.
 */
export function formatFileSize(bytes: number, locale: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';

  const format = (value: number, fractionDigits: number) =>
    new Intl.NumberFormat(locale, {
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    }).format(value);

  if (bytes < 1024) return `${format(bytes, 0)} B`;
  if (bytes < 1024 * 1024) return `${format(bytes / 1024, 0)} KB`;
  return `${format(bytes / (1024 * 1024), 1)} MB`;
}

/** Display grouping only. The raw value is what gets copied and submitted. */
export function groupDigits(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits === '') return value;
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}
